import { Grid } from '@material-ui/core';
import { useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import QCXFormSubtitle from '../../components/form-title/QCXFormSubtitle';
import QCXFormTitle from '../../components/form-title/QCXFormTitle';
import DominioDinamicoV2 from './components-fields-v2/DominioDinamicoV2';
import ListaEstaticaV2 from './components-fields-v2/ListaEstaticaV2';
import ValorInteiroV2 from './components-fields-v2/ValorInteiroV2';
import ValorRealV2 from './components-fields-v2/ValorRealV2';
import ValorTextoV2 from './components-fields-v2/ValorTextoV2';
import type { CombinacaoValida } from './components-fields-v2/types';

export interface CodigoNomeTributoDTO {
  codigo: string;
  nome: string;
}

export interface CodigoNomeRegimeDTO {
  codigo: string;
  nome: string;
}

export interface FundamentoLegalResponseDTO {
  codigo: string;
  nome: string;
  tipo: string;
}

export interface AtributoMercadoriaDTO {
  codigo: string;
  descricaoCodigo: string;
  tipoCodigo: string;
  valor: string;
  descricaoValor: string;
}

export interface AtributoTratamentoNormalizado extends AtributoMercadoriaDTO {
  codTratamento: string;
  dominio: Array<{
    valor: string;
    descricao: string;
  }>;
}

export interface MercadoriaTratamentoTributarioDTO {
  atributos: AtributoMercadoriaDTO[];
}

export interface TratamentoTributarioResponseDTO {
  tributo: CodigoNomeTributoDTO;
  regime: CodigoNomeRegimeDTO;
  fundamentoLegal: FundamentoLegalResponseDTO;
  isProcessado: boolean;
  mercadorias: MercadoriaTratamentoTributarioDTO[];
  possuiAtributosAdicionais: string;
  codeTratamento: string | null;
}

type QCXAtributosTratamentoTributarioDuimpV2Props = {
  name: string;
  tributos: TratamentoTributarioResponseDTO[];
  isConsult?: boolean;
};

const atributoEstaVisivel = (
  atributo: AtributoTratamentoNormalizado,
  combinacoesValidas: CombinacaoValida[],
  valoresFormulario: Record<string, { valor?: unknown }>
) => {
  const atributosSelecionados = Object.entries(valoresFormulario).filter(
    ([, valor]) => valor?.valor !== undefined && valor?.valor !== null && valor?.valor !== ''
  );

  if (!atributosSelecionados.length || !combinacoesValidas.length) return true;

  return combinacoesValidas.some((combinacao) => {
    const combinacaoCompativel = atributosSelecionados.every(([codigo, valor]) => {
      const atributoNaCombinacao = combinacao.find((item) => item.codigo === codigo);

      return (
        !atributoNaCombinacao ||
        atributoNaCombinacao.valor === String(valor.valor) ||
        atributoNaCombinacao.valor === ''
      );
    });

    return combinacaoCompativel && combinacao.some((item) => item.codigo === atributo.codigo);
  });
};

export default function QCXAtributosTratamentoTributarioDuimpV2({
  name,
  tributos,
  isConsult = false,
}: QCXAtributosTratamentoTributarioDuimpV2Props) {
  const form = useForm();
  const { values } = useFormState();
  const tratamentosNormalizados = useMemo(
    () =>
      (tributos || [])
        .map((tratamento) => {
          const codTratamento =
            tratamento.codeTratamento ||
            `${tratamento.tributo.codigo};${tratamento.regime.codigo};${tratamento.fundamentoLegal.codigo}`;
          const atributosPorCodigo = new Map<string, AtributoTratamentoNormalizado>();
          const combinacoesValidas: CombinacaoValida[] = [];

          tratamento.mercadorias?.forEach((mercadoria) => {
            const combinacao = mercadoria.atributos.map((atributo) => ({
              codigo: atributo.codigo,
              valor: atributo.valor,
            }));

            combinacoesValidas.push(combinacao);

            mercadoria.atributos.forEach((atributo) => {
              const atributoNormalizado = atributosPorCodigo.get(atributo.codigo);

              if (atributoNormalizado) {
                if (!atributoNormalizado.dominio.some((opcao) => opcao.valor === atributo.valor)) {
                  atributoNormalizado.dominio.push({
                    valor: atributo.valor,
                    descricao: atributo.descricaoValor,
                  });
                }
                return;
              }

              atributosPorCodigo.set(atributo.codigo, {
                ...atributo,
                codTratamento,
                dominio: [
                  {
                    valor: atributo.valor,
                    descricao: atributo.descricaoValor,
                  },
                ],
              });
            });
          });

          return {
            tratamento,
            codTratamento,
            atributos: Array.from(atributosPorCodigo.values()),
            combinacoesValidas,
          };
        })
        .filter((t) => t.atributos.length !== 0),
    [tributos]
  );

  const valoresFormulario = (values?.ignorableFields?.tratamentosTtce?.metadados?.valores || {}) as Record<
    string,
    { valor?: unknown }
  >;

  useEffect(() => {
    const codigosAtributos = new Set<string>();
    const codigosAtributosVisiveis = new Set<string>();

    tratamentosNormalizados.forEach(({ atributos, combinacoesValidas }) => {
      atributos.forEach((atributo) => {
        codigosAtributos.add(atributo.codigo);

        if (atributoEstaVisivel(atributo, combinacoesValidas, valoresFormulario)) {
          codigosAtributosVisiveis.add(atributo.codigo);
        }
      });
    });

    const valoresAtualizados = Object.fromEntries(
      Object.entries(valoresFormulario).filter(
        ([codigo]) => !codigosAtributos.has(codigo) || codigosAtributosVisiveis.has(codigo)
      )
    );

    if (Object.keys(valoresAtualizados).length !== Object.keys(valoresFormulario).length) {
      form.change(name, valoresAtualizados);
    }
  }, [form, name, tratamentosNormalizados, valoresFormulario]);

  const renderAtributo = (atributo: AtributoTratamentoNormalizado, combinacoesValidas: CombinacaoValida[]) => {
    const tipoCodigo = atributo.tipoCodigo
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const props = {
      atributo,
      basePath: name,
      combinacoesValidas,
      disabled: isConsult,
    };

    if (tipoCodigo === 'dominio dinamico') {
      return <DominioDinamicoV2 {...props} />;
    }

    if (tipoCodigo === 'lista estatica') {
      return <ListaEstaticaV2 {...props} />;
    }

    if (tipoCodigo === 'valor inteiro' || tipoCodigo === 'numero inteiro') {
      return <ValorInteiroV2 {...props} />;
    }

    if (tipoCodigo === 'valor real' || tipoCodigo === 'numero real') {
      return <ValorRealV2 {...props} />;
    }

    return <ValorTextoV2 {...props} />;
  };

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormTitle title={'Atributos de Tratamento Tributario'.toUpperCase()} />
      </Grid>

      {tratamentosNormalizados.map(({ tratamento, codTratamento, atributos, combinacoesValidas }) => (
        <Grid item xs={12} key={codTratamento}>
          <QCXFormSubtitle
            title={`${tratamento.tributo.codigo} - ${tratamento.tributo.nome} | ${tratamento.regime.codigo} - ${tratamento.regime.nome} | ${tratamento.fundamentoLegal.codigo} - ${tratamento.fundamentoLegal.nome}`}
          />
          <Grid container spacing={2}>
            {atributos.map((atributo) => {
              if (!atributoEstaVisivel(atributo, combinacoesValidas, valoresFormulario)) return null;

              return (
                <Grid style={{ marginTop: 16 }} item xs={12} sm={6} md={4} key={`${codTratamento}-${atributo.codigo}`}>
                  {renderAtributo(atributo, combinacoesValidas)}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ))}
    </>
  );
}
